import { Capacitor, CapacitorHttp, HttpResponse } from '@capacitor/core';
import { Photo, Gear, Video, Message, Profile, SiteSettings, NewsItem, Resource, Testimonial, AstroEvent, RecentTarget, AdminUsersResponse, NewsFeedState, NewsFeedCollectResult, NewsFeedPublishResult, NewsFeedSource } from '@/types';

const BASE_URL = Capacitor.isNativePlatform()
  ? 'https://www.stellarskyimager.fr'
  : '';

// Helper pour le retry avec backoff exponentiel (uniquement pour GET)
const fetchWithRetry = async (url: string, options: RequestInit, retries: number = 2, backoff: number = 500): Promise<Response> => {
  try {
    const res = await fetch(url, options);
    // On ne retry que les erreurs serveur 5xx ou si la requête a échoué au niveau réseau (catch)
    if (!res.ok && res.status >= 500 && retries > 0 && options.method === 'GET') {
      console.warn(`Retrying fetch for ${url} (${retries} left)...`);
      await new Promise(resolve => setTimeout(resolve, backoff));
      return fetchWithRetry(url, options, retries - 1, backoff * 2);
    }
    return res;
  } catch (e) {
    if (retries > 0 && options.method === 'GET') {
      console.warn(`Network error, retrying ${url} (${retries} left)...`);
      await new Promise(resolve => setTimeout(resolve, backoff));
      return fetchWithRetry(url, options, retries - 1, backoff * 2);
    }
    throw e;
  }
};

// Helper générique pour les appels API
const request = async <T>(url: string, method: string = 'GET', body?: any): Promise<T> => {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;

  if (Capacitor.isNativePlatform()) {
    const options = {
      url: fullUrl,
      method: method as any,
      headers: { 'Content-Type': 'application/json' },
      data: body,
      connectTimeout: 15000,
      readTimeout: 15000
    };

    const performCapacitorRequest = async (currentRetries: number): Promise<HttpResponse> => {
      try {
        const response: HttpResponse = await CapacitorHttp.request(options);
        if (response.status >= 500 && currentRetries > 0 && method === 'GET') {
          throw new Error(`Server Error: ${response.status}`);
        }
        return response;
      } catch (e) {
        if (currentRetries > 0 && method === 'GET') {
          await new Promise(resolve => setTimeout(resolve, 1000 * (3 - currentRetries)));
          return performCapacitorRequest(currentRetries - 1);
        }
        throw e;
      }
    };

    try {
      const response = await performCapacitorRequest(2);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.data?.error || `API Error: ${response.status}`);
      }
      
      // Mise en cache des données réussies (Mobile)
      if (url === '/api/settings' || url.includes('/api/youtube/stats') || url.includes('/api/planner/stats') || url === '/api/newsletter/settings') {
        localStorage.setItem(`cache_${url}`, JSON.stringify(response.data));
      }

      return response.data;
    } catch (e: any) {
      const errorDetail = e.message || (typeof e === 'object' ? JSON.stringify(e) : e);
      console.warn(`CapacitorHttp error for ${fullUrl}:`, errorDetail);
      
      // Tentative de récupération depuis le cache
      const cached = localStorage.getItem(`cache_${url}`);
      if (cached) {
        console.info(`Returning cached data for ${url}`);
        return JSON.parse(cached) as T;
      }

      // Fallbacks globaux pour mobile
      if (url.includes('/api/settings')) {
        return { siteName: 'StellarSkyImager', primaryColor: '#D4AF37', maintenanceMode: false, observationNights: '789' } as unknown as T;
      }
      if (url === '/api/newsletter/settings') {
        return { newsletterEnabled: true } as unknown as T;
      }
      if (url.includes('/api/youtube/stats')) {
        return { subscriberCount: 250000, videoCount: 150 } as unknown as T;
      }
      if (url.includes('/api/planner/stats')) {
        return { plannedNights: 4200 } as unknown as T;
      }
      if (url.includes('/api/planner/count')) {
        return { count: 0 } as unknown as T;
      }
      if (url === '/api/testimonials' || url === '/api/photos' || url === '/api/videos') {
        return [] as unknown as T;
      }
      
      throw new Error(`Erreur réseau: ${errorDetail}`);
    }
  }

  const options: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include'
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  try {
    const res = await fetchWithRetry(fullUrl, options);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `API Error: ${res.statusText}`);
    }
    const data = await res.json();

    // Mise en cache des données réussies (Web)
    if (url === '/api/settings' || url.includes('/api/youtube/stats') || url.includes('/api/planner/stats') || url === '/api/newsletter/settings') {
      localStorage.setItem(`cache_${url}`, JSON.stringify(data));
    }

    return data;
  } catch (e: any) {
    // Tentative de récupération depuis le cache en cas d'erreur réseau
    const cached = localStorage.getItem(`cache_${url}`);
    if (cached) {
      console.info(`Returning cached data for ${url} due to error:`, e.message);
      return JSON.parse(cached) as T;
    }

    // Fallbacks gracieux en mode hors ligne / Erreur fatale
    if (url.includes('/api/settings')) {
      return {
        siteName: 'StellarSkyImager',
        primaryColor: '#D4AF37',
        maintenanceMode: false,
        sentinelPublic: false,
        observationNights: '789'
      } as unknown as T;
    }

    if (url === '/api/newsletter/settings') {
      return { newsletterEnabled: true } as unknown as T;
    }

    if (url.includes('/api/youtube/stats')) {
      return { subscriberCount: 250000, videoCount: 150 } as unknown as T;
    }

    if (url.includes('/api/planner/stats')) {
      return { plannedNights: 4200 } as unknown as T;
    }

    if (url.includes('/api/planner/count')) {
      return { count: 0 } as unknown as T;
    }

    if (url === '/api/testimonials' || url === '/api/photos' || url === '/api/videos') {
      console.warn(`Returning empty list fallback for ${url} due to error:`, e.message);
      return [] as unknown as T;
    }

    // Si on a déjà volontairement levé une Error dans le try avec le message métier Zod/Captcha, on la relance telle quelle.
    if (e instanceof Error && !e.message.startsWith('NetworkError') && !e.message.includes('fetch failed') && !e.message.includes('Impossible de joindre')) {
      throw e;
    }

    throw new Error(`Erreur réseau: Impossible de joindre le serveur. (${e.message || 'Hors ligne'})`);
  }
};

export const api = {
  auth: {
    login: (creds: { username: string, password: string }) => request<{ success: boolean, username: string }>('/api/login', 'POST', creds),
    logout: () => request<{ success: boolean }>('/api/logout', 'POST'),
    // Vérification de session admin : endpoint dédié, distinct du `/api/me` du système d'auth
    // utilisateur Sentinel (qui masque le `/api/me` admin côté serveur, cf. index.js).
    me: () => request<{ success: boolean, user: any }>('/api/admin/session', 'GET'),
  },

  photos: {
    list: () => request<Photo[]>('/api/photos'),
    save: (photo: Photo) => request<void>('/api/photos', 'POST', photo),
    delete: (id: string) => request<void>(`/api/photos/${id}`, 'DELETE'),
  },

  gear: {
    list: () => request<Gear[]>('/api/gear'),
    save: (item: Gear) => request<void>('/api/gear', 'POST', item),
    delete: (id: string) => request<void>(`/api/gear/${id}`, 'DELETE'),
  },

  videos: {
    list: () => request<Video[]>('/api/videos'),
    save: (video: Video) => request<void>('/api/videos', 'POST', video),
    delete: (id: string) => request<void>(`/api/videos/${id}`, 'DELETE'),

    getByYoutubeId: async (youtubeId: string): Promise<Video | undefined> => {
      const videos = await request<Video[]>('/api/videos');
      return videos.find(v => v.youtubeId === youtubeId);
    },

    syncFromYouTube: async (channelId?: string): Promise<{ success: boolean; count: number; error?: string }> => {
      try {
        // 1. Récupérer les vidéos depuis l'API YouTube via le serveur proxy
        // Le serveur proxy est AUTHENTIFIÉ maintenant pour le sync.
        let url = '/api/youtube/sync';
        if (channelId) url += `?channelId=${encodeURIComponent(channelId)}`;

        // On utilise request ici pour passer les credentials aussi à ce fetch manuel si besoin
        // Mais request fait déjà fetch. 
        // Cependant, syncFromYouTube faisait un fetch direct. On va utiliser request<Video[]> pour bénéficier des cookies
        // Attention, url de sync renvoie Video[] directement. 

        const ytVideos = await request<any[]>(url, 'GET');

        // 2. Récupérer les vidéos existantes en BDD
        const existingVideos = await request<Video[]>('/api/videos');
        const merged = [...existingVideos];
        let addedCount = 0;

        // 3. Fusionner (Update existants, Create nouveaux)
        for (const ytVideo of ytVideos) {
          const index = merged.findIndex(v => v.youtubeId === ytVideo.youtubeId);

          if (index >= 0) {
            // Update (preserve custom fields like isFeatured)
            merged[index] = {
              ...merged[index],
              title: ytVideo.title,
              thumbnailUrl: ytVideo.thumbnailUrl,
              publishedAt: ytVideo.publishedAt,
              playlist: ytVideo.playlist
            };
          } else {
            // New Video
            merged.push({
              id: Date.now().toString() + '-' + addedCount,
              youtubeId: ytVideo.youtubeId,
              title: ytVideo.title,
              description: ytVideo.description || '',
              thumbnailUrl: ytVideo.thumbnailUrl,
              publishedAt: ytVideo.publishedAt,
              playlist: ytVideo.playlist,
              isVisible: true,
              isFeatured: false
            });
            addedCount++;
          }
        }

        // 4. Sauvegarder la liste mise à jour en BDD (Batch)
        await request('/api/videos/batch', 'POST', merged);
        return { success: true, count: ytVideos.length };

      } catch (error: any) {
        return { success: false, count: 0, error: error.message || 'Erreur réseau' };
      }
    }
  },

  messages: {
    list: () => request<Message[]>('/api/messages'),
    getCaptcha: () => request<{ question: string; token: string }>('/api/captcha'),
    send: (msg: Omit<Message, 'id' | 'date' | 'status'>, captchaToken: string, captchaAnswer: string) => {
      const fullMsg = {
        ...msg,
        id: Date.now().toString(),
        date: new Date().toISOString(),
        status: 'unread' as const, // cast for TS
        captchaToken,
        captchaAnswer
      };
      return request<void>('/api/messages', 'POST', fullMsg);
    },
    reply: (id: string, content: string) => request<void>(`/api/messages/${id}/reply`, 'POST', { content }),
    updateStatus: (id: string, status: Message['status']) => request<void>(`/api/messages/${id}/status`, 'PUT', { status }),
    delete: (id: string) => request<void>(`/api/messages/${id}`, 'DELETE'),
  },

  profile: {
    get: () => request<Profile>('/api/profile'),
    update: (data: Profile) => request<void>('/api/profile', 'POST', data),
  },

  admin: {
    translate: (text: string, targetLangs?: string[]) => 
      request<Record<string, string>>('/api/admin/translate', 'POST', { text, targetLangs }),
  },

  news: {
    list: () => request<NewsItem[]>('/api/news'),
    save: (item: NewsItem) => request<void>('/api/news', 'POST', item),
    delete: (id: string) => request<void>(`/api/news/${id}`, 'DELETE'),
  },

  // [D15] Actualités automatiques : brouillons issus des flux RSS
  newsFeed: {
    get: () => request<NewsFeedState>('/api/admin/news-feed'),
    collect: () => request<NewsFeedCollectResult>('/api/admin/news-feed/collect', 'POST'),
    publish: (ids: number[]) => request<NewsFeedPublishResult>('/api/admin/news-feed/publish', 'POST', { ids }),
    dismiss: (ids: number[]) => request<{ dismissed: number }>('/api/admin/news-feed/dismiss', 'POST', { ids }),
    setWindowDays: (windowDays: number) => request<{ windowDays: number }>('/api/admin/news-feed/settings', 'PATCH', { windowDays }),
    createSource: (source: Pick<NewsFeedSource, 'name' | 'feed_url' | 'image_credit' | 'license' | 'lang' | 'keyword_filter'>) =>
      request<NewsFeedSource>('/api/admin/news-feed/sources', 'POST', source),
    setSourceActive: (id: number, active: boolean) => request<NewsFeedSource>(`/api/admin/news-feed/sources/${id}`, 'PATCH', { active }),
  },

  resources: {
    list: () => request<Resource[]>('/api/resources'),
    save: (item: Resource) => request<void>('/api/resources', 'POST', item),
    delete: (id: string) => request<void>(`/api/resources/${id}`, 'DELETE'),
    download: (id: string) => request<void>(`/api/resources/${id}/download`, 'POST'),
  },

  testimonials: {
    // Public: liste des témoignages approuvés
    list: () => request<Testimonial[]>('/api/testimonials'),
    // Public: créer un témoignage (statut pending)
    create: (data: { author_name: string; message: string; rating: number }) =>
      request<{ success: boolean; testimonial: Testimonial }>('/api/testimonials', 'POST', data),
    // Admin: liste de tous les témoignages
    listAll: () => request<Testimonial[]>('/api/admin/testimonials'),
    // Admin: compter les témoignages en attente
    countPending: () => request<{ count: number }>('/api/admin/testimonials/pending-count'),
    // Admin: modérer un témoignage
    updateStatus: (id: string, status: Testimonial['status']) =>
      request<void>(`/api/admin/testimonials/${id}/status`, 'PUT', { status }),
    // Admin: supprimer un témoignage
    delete: (id: string) => request<void>(`/api/admin/testimonials/${id}`, 'DELETE'),
  },

  settings: {
    get: () => request<SiteSettings>('/api/settings'),
    update: (data: SiteSettings) => request<void>('/api/settings', 'POST', data),
  },

  newsletter: {
    subscribe: (data: any) => request<{ success: boolean; subscriber: any }>('/api/newsletter/subscribe', 'POST', data),
    getSettings: () => request<{ newsletterEnabled: boolean }>('/api/newsletter/settings'),
    updateSettings: (newsletterEnabled: boolean) => request<void>('/api/newsletter/settings', 'PATCH', { newsletterEnabled }),
    // Admin
    listSubscribers: () => request<any[]>('/api/admin/newsletter/subscribers'),
    getStats: () => request<{ total: number; active: number }>('/api/admin/newsletter/stats'),
    deleteSubscriber: (id: string) => request<void>(`/api/admin/newsletter/subscribers/${id}`, 'DELETE'),
    preview: (email: string) => `/api/admin/newsletter/preview?email=${encodeURIComponent(email)}`, // Retourne l'URL pour un iframe ou window.open
    previewDraft: (draft: { welcome?: boolean; newsletterIntroText?: string; newsletterWelcomeText?: string; newsletterFrequencyText?: string; newsletterCustomText?: string; newsletterResourceTitle?: string; newsletterResourceText?: string; newsletterResourceUrl?: string; newsletterContactEmail?: string; newsletterPhotos?: unknown[] }) =>
      request<{ html: string }>('/api/admin/newsletter/preview-draft', 'POST', draft),
    testSend: (email: string) => request<{ success: boolean; message: string }>('/api/admin/newsletter/test-send', 'POST', { email }),
  },
  
  youtube: {
    getStats: () => request<{ subscriberCount: number; videoCount: number }>('/api/youtube/stats'),
  },

  planner: {
    getStats: () => request<{ plannedNights: number }>('/api/planner/stats'),
    getLiveCount: () => request<{ count: number }>('/api/planner/count'),
    getRecentTargets: () => request<RecentTarget[]>('/api/planner/recent-targets'),
  },

  // [US 6.29] Administration des comptes Sentinel (liste + gestes commerciaux). Admin uniquement.
  adminUsers: {
    list: () => request<AdminUsersResponse>('/api/admin/sentinel-users'),
    makeFounder: (id: string) =>
      request<{ ok: boolean; alreadyFounder: boolean }>(`/api/admin/sentinel-users/${id}/founder`, 'POST'),
    revokeFounder: (id: string) =>
      request<{ ok: boolean; wasFounder: boolean }>(`/api/admin/sentinel-users/${id}/founder`, 'DELETE'),
    // `months` omis = 3 (valeur par défaut portée par le serveur).
    grantPremium: (id: string, months?: number) =>
      request<{ ok: boolean; months: number; grantedUntil: string; premiumUntil: string | null }>(
        `/api/admin/sentinel-users/${id}/premium-grant`, 'POST', months ? { months } : {}),
    revokeGrant: (id: string) =>
      request<{ ok: boolean; hadGrant: boolean; premiumUntil: string | null }>(
        `/api/admin/sentinel-users/${id}/premium-grant`, 'DELETE'),
  },

  astroEvents: {
    listUpcoming: (limit = 3) => request<AstroEvent[]>(`/api/astro-events?limit=${limit}`),
    listByYear: (year: number) => request<AstroEvent[]>(`/api/astro-events?year=${year}`),
    // Admin
    listAll: () => request<AstroEvent[]>('/api/admin/astro-events'),
    save: (event: Omit<AstroEvent, 'id' | 'created_at'>) => request<void>('/api/admin/astro-events', 'POST', event),
    update: (id: number, event: Partial<AstroEvent>) => request<void>(`/api/admin/astro-events/${id}`, 'PUT', { ...event, id }),
    delete: (id: number) => request<void>(`/api/admin/astro-events/${id}`, 'DELETE'),
    prefill: (year: number) => request<{ success: boolean; inserted: number; year: number }>('/api/admin/astro-events/prefill', 'POST', { year }),
  },
};
